import type { AsyncAcceptedResponse, Release } from "@webyonetim/contracts";
import { useEffect, useState } from "react";

import { Button, SelectField, TextField } from "../../components/ui/Controls";
import {
  AlertBanner,
  EmptyState,
  ErrorState,
  InlineResult,
  Skeleton,
  StatusBadge,
  type StatusCode,
} from "../../components/ui/Feedback";
import { CodeValue, ResponsiveData, type DataColumn } from "../../components/ui/ResponsiveData";
import {
  ApiError,
  releasesApi,
  type DeploymentView,
  type EnvironmentReleaseTruth,
  type PreviewView,
} from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { JobProgress } from "./JobProgress";

export interface ReleaseHistoryProps {
  readonly projectId: string;
}

interface RollbackState {
  readonly release: Release;
  readonly environments: readonly EnvironmentReleaseTruth[];
  readonly selectedEnvironmentId: string;
}

export function ReleaseHistory({ projectId }: ReleaseHistoryProps) {
  const [releases, setReleases] = useState<readonly Release[]>([]);
  const [deployments, setDeployments] = useState<readonly DeploymentView[]>([]);
  const [previews, setPreviews] = useState<readonly PreviewView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [rollback, setRollback] = useState<RollbackState | undefined>();
  const [rollbackConfirmation, setRollbackConfirmation] = useState("");
  const [mutationBusy, setMutationBusy] = useState<string | undefined>();
  const [mutationError, setMutationError] = useState<string | undefined>();
  const [accepted, setAccepted] = useState<AsyncAcceptedResponse | undefined>();
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>();

  const refresh = async (signal?: AbortSignal) => {
    setError(undefined);
    try {
      const [releasePage, deploymentPage, previewPage] = await Promise.all([
        releasesApi.list(projectId, signal),
        releasesApi.deployments(projectId, signal),
        releasesApi.previews(projectId, signal),
      ]);
      setReleases(releasePage.items);
      setDeployments(deploymentPage.items);
      setPreviews(previewPage.items);
    } catch (caught: unknown) {
      if (signal?.aborted === true) return;
      setError(problemMessage(caught));
    } finally {
      if (signal?.aborted !== true) setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => {
      controller.abort();
    };
  }, [projectId]);

  const releaseColumns: readonly DataColumn<Release>[] = [
    {
      key: "release",
      header: "Release",
      render: (release) => (
        <div className="stack stack--tight">
          <CodeValue>{release.id}</CodeValue>
          <span className="muted">{release.originKind === "build" ? "Build" : "Hazır paket"}</span>
        </div>
      ),
    },
    {
      key: "source",
      header: "Kaynak",
      render: (release) => (
        <div className="stack stack--tight">
          <span>{release.source.kind === "git" ? release.source.ref : "ZIP"}</span>
          <CodeValue>
            {release.source.kind === "git" ? release.source.commitSha : release.source.digest}
          </CodeValue>
        </div>
      ),
    },
    {
      key: "artifact",
      header: "Artifact",
      render: (release) => (
        <div className="stack stack--tight">
          <span>{release.artifact.kind === "image" ? "Container image" : "Statik paket"}</span>
          <CodeValue>{release.artifact.digest}</CodeValue>
        </div>
      ),
    },
    {
      key: "created",
      header: "Oluşturuldu",
      render: (release) => formatDateTime(release.finalizedAt),
    },
    {
      key: "actions",
      header: "İşlemler",
      render: (release) => (
        <Button
          busy={mutationBusy === `rollback-open:${release.id}`}
          onClick={() => {
            void openRollback(release);
          }}
          variant="secondary"
        >
          Bu release'e geri dön
        </Button>
      ),
    },
  ];

  const openRollback = async (release: Release) => {
    setMutationBusy(`rollback-open:${release.id}`);
    setMutationError(undefined);
    try {
      const detail = await releasesApi.detail(projectId, release.id);
      const selected =
        detail.environments.find((environment) => environment.activeReleaseId !== undefined) ??
        detail.environments[0];
      setRollback({
        release,
        environments: detail.environments,
        selectedEnvironmentId: selected?.environmentId ?? "",
      });
      setRollbackConfirmation("");
    } catch (caught: unknown) {
      setMutationError(problemMessage(caught));
    } finally {
      setMutationBusy(undefined);
    }
  };

  const submitRollback = async () => {
    if (rollback === undefined) return;
    const environment = rollback.environments.find(
      (candidate) => candidate.environmentId === rollback.selectedEnvironmentId,
    );
    if (
      environment?.activeReleaseId === undefined ||
      rollbackConfirmation !== rollback.release.id
    ) {
      return;
    }
    setMutationBusy(`rollback:${rollback.release.id}`);
    setMutationError(undefined);
    try {
      const result = await releasesApi.rollback(projectId, environment.environmentId, {
        targetReleaseId: rollback.release.id,
        expectedActiveReleaseId: environment.activeReleaseId,
        expectedDesiredGeneration: environment.desiredGeneration,
        expectedActivationVersion: environment.activationVersion,
      });
      setAccepted(result);
      setSelectedJobId(result.jobId);
      setRollback(undefined);
      await refresh();
    } catch (caught: unknown) {
      setMutationError(problemMessage(caught));
    } finally {
      setMutationBusy(undefined);
    }
  };

  const retryDeployment = async (deployment: DeploymentView) => {
    setMutationBusy(`retry:${deployment.id}`);
    setMutationError(undefined);
    try {
      const result = await releasesApi.retry(projectId, deployment.id, deployment.revision);
      setAccepted(result);
      setSelectedJobId(result.jobId);
      await refresh();
    } catch (caught: unknown) {
      setMutationError(problemMessage(caught));
    } finally {
      setMutationBusy(undefined);
    }
  };

  const cleanupPreview = async (preview: PreviewView) => {
    setMutationBusy(`preview:${preview.id}`);
    setMutationError(undefined);
    try {
      const result = await releasesApi.cleanupPreview(projectId, preview.id, {
        confirmationId: preview.id,
        expectedRevision: preview.revision,
        expectedDesiredGeneration: preview.desiredGeneration,
      });
      setAccepted(result);
      setSelectedJobId(result.jobId);
      await refresh();
    } catch (caught: unknown) {
      setMutationError(problemMessage(caught));
    } finally {
      setMutationBusy(undefined);
    }
  };

  if (loading) return <Skeleton label="Release geçmişi yükleniyor" />;
  if (error !== undefined) {
    return (
      <ErrorState
        onRetry={() => {
          setLoading(true);
          void refresh();
        }}
      />
    );
  }

  const selectedRollbackEnvironment = rollback?.environments.find(
    (environment) => environment.environmentId === rollback.selectedEnvironmentId,
  );

  return (
    <div className="stack">
      {mutationError === undefined ? null : (
        <AlertBanner heading="İşlem başlatılamadı" tone="error">
          <p>{mutationError}</p>
          <p>Mevcut release korunur; doğrulanmış trafik değiştirilmez.</p>
        </AlertBanner>
      )}
      {accepted === undefined ? null : (
        <InlineResult requestId={accepted.requestId} tone="info">
          İşlem kabul edildi. Sonuç agent tarafından doğrulanıyor.
        </InlineResult>
      )}

      <section aria-labelledby="release-history-heading" className="card stack">
        <div>
          <p className="eyebrow">Değişmez kayıtlar</p>
          <h2 id="release-history-heading">Release geçmişi</h2>
          <p className="muted">
            Release değişmez; dağıtım denemeleri ve trafik aktivasyonu ayrı kaydedilir.
          </p>
        </div>
        {releases.length === 0 ? (
          <EmptyState
            body="Temiz bir kaynak kabul edilip build tamamlandığında release burada görünür."
            heading="Henüz release yok"
          />
        ) : (
          <ResponsiveData
            caption="Projenin değişmez release kayıtları"
            columns={releaseColumns}
            getKey={(release) => release.id}
            items={releases}
            mobileHeading={(release) => release.id}
          />
        )}
      </section>

      <section aria-labelledby="deployment-history-heading" className="card stack">
        <div>
          <p className="eyebrow">Değişebilir denemeler</p>
          <h2 id="deployment-history-heading">Dağıtım denemeleri</h2>
          <p className="muted">
            “İstenen release” kontrol düzlemi niyetidir; gerçek trafik Phase 3/4 sağlık ve
            aktivasyon kanıtıyla doğrulanır.
          </p>
        </div>
        {deployments.length === 0 ? (
          <p>Henüz dağıtım denemesi yok.</p>
        ) : (
          <div className="stack">
            {deployments.map((deployment) => (
              <article className="card stack stack--tight" key={deployment.id}>
                <div className="split-row">
                  <div>
                    <h3>{deploymentKindLabel(deployment.kind)}</h3>
                    <CodeValue>{deployment.releaseId}</CodeValue>
                  </div>
                  <StatusBadge
                    label={deploymentStatusLabel(deployment.status)}
                    status={deploymentStatusCode(deployment.status)}
                  />
                </div>
                <p className="muted">Trafik doğrulandı: Hayır — yalnız kontrol düzlemi niyeti</p>
                {deployment.failureCode === undefined ? null : (
                  <AlertBanner heading="Dağıtım etkinleştirilmedi" tone="warning">
                    <p className="technical">Hata kodu: {deployment.failureCode}</p>
                    <p>Sağlık veya CAS doğrulaması geçmedi; mevcut release korunur.</p>
                  </AlertBanner>
                )}
                <div className="cluster">
                  {(deployment.status === "failed" || deployment.status === "cancelled") && (
                    <Button
                      busy={mutationBusy === `retry:${deployment.id}`}
                      onClick={() => {
                        void retryDeployment(deployment);
                      }}
                      variant="secondary"
                    >
                      Yeniden dene
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setSelectedJobId(deployment.jobId);
                    }}
                    variant="quiet"
                  >
                    İşi izle
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="preview-history-heading" className="card stack">
        <div>
          <p className="eyebrow">Pull request ortamları</p>
          <h2 id="preview-history-heading">Önizlemeler</h2>
        </div>
        {previews.length === 0 ? (
          <p>Etkin veya kapanmakta olan önizleme yok.</p>
        ) : (
          <div className="stack">
            {previews.map((preview) => (
              <article className="card stack stack--tight" key={preview.id}>
                <div className="split-row">
                  <h3>PR #{preview.pullRequestNumber}</h3>
                  <StatusBadge
                    label={previewStatusLabel(preview.status)}
                    status={previewStatusCode(preview.status)}
                  />
                </div>
                <dl className="definition-grid">
                  <div>
                    <dt>HEAD</dt>
                    <dd>
                      <CodeValue>{preview.headCommitSha}</CodeValue>
                    </dd>
                  </div>
                  <div>
                    <dt>İstenen nesil</dt>
                    <dd>{preview.desiredGeneration}</dd>
                  </div>
                  <div>
                    <dt>Hostname niyeti</dt>
                    <dd>{preview.hostnameIntent}</dd>
                  </div>
                  <div>
                    <dt>Gerçek trafik</dt>
                    <dd>Henüz doğrulanmadı</dd>
                  </div>
                </dl>
                {preview.status === "removed" || preview.status === "closing" ? null : (
                  <Button
                    busy={mutationBusy === `preview:${preview.id}`}
                    onClick={() => {
                      void cleanupPreview(preview);
                    }}
                    variant="destructive"
                  >
                    Önizlemeyi kapat
                  </Button>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {selectedJobId === undefined ? null : (
        <div id={`job-${selectedJobId}`}>
          <JobProgress jobId={selectedJobId} />
        </div>
      )}

      {rollback === undefined ? null : (
        <>
          <div aria-hidden="true" className="dialog-overlay" />
          <section
            aria-labelledby="rollback-heading"
            aria-modal="true"
            className="dialog-content"
            role="dialog"
          >
            <h2 id="rollback-heading">Release'e geri dön</h2>
            <AlertBanner heading="Trafik değişikliği" tone="warning">
              <p>
                Hedef artifact: <CodeValue>{rollback.release.artifact.digest}</CodeValue>
              </p>
              <p>Yeniden build yapılmaz. Sağlık kontrolü geçmeden trafik değiştirilmez.</p>
            </AlertBanner>
            <SelectField
              label="Ortam"
              onChange={(event) => {
                setRollback({
                  ...rollback,
                  selectedEnvironmentId: event.currentTarget.value,
                });
              }}
              value={rollback.selectedEnvironmentId}
            >
              {rollback.environments.map((environment) => (
                <option key={environment.environmentId} value={environment.environmentId}>
                  {environment.name}
                </option>
              ))}
            </SelectField>
            <dl className="definition-grid">
              <div>
                <dt>Mevcut istenen release</dt>
                <dd>
                  {selectedRollbackEnvironment?.activeReleaseId === undefined ? (
                    "Yok"
                  ) : (
                    <CodeValue>{selectedRollbackEnvironment.activeReleaseId}</CodeValue>
                  )}
                </dd>
              </div>
              <div>
                <dt>Beklenen nesil</dt>
                <dd>{selectedRollbackEnvironment?.desiredGeneration ?? "—"}</dd>
              </div>
            </dl>
            <TextField
              autoComplete="off"
              help="Onaylamak için hedef release kimliğini eksiksiz yazın."
              label="Hedef release kimliği"
              onChange={(event) => {
                setRollbackConfirmation(event.currentTarget.value);
              }}
              value={rollbackConfirmation}
            />
            <div className="form-actions">
              <Button
                disabled={
                  selectedRollbackEnvironment?.activeReleaseId === undefined ||
                  rollbackConfirmation !== rollback.release.id
                }
                busy={mutationBusy === `rollback:${rollback.release.id}`}
                onClick={() => {
                  void submitRollback();
                }}
                variant="destructive"
              >
                Geri dönüşü başlat
              </Button>
              <Button
                onClick={() => {
                  setRollback(undefined);
                }}
                variant="secondary"
              >
                Vazgeç
              </Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function deploymentStatusCode(status: DeploymentView["status"]): StatusCode {
  if (status === "active") return "succeeded";
  if (status === "failed") return "failed";
  if (status === "cancelled" || status === "superseded") return "cancelled";
  if (status === "reconciling") return "reconciling";
  if (status === "requested") return "queued";
  return "running";
}

function deploymentStatusLabel(status: DeploymentView["status"]): string {
  const labels: Readonly<Record<DeploymentView["status"], string>> = {
    requested: "Kabul edildi",
    staging: "Hazırlanıyor",
    checking: "Sağlık kontrolü",
    ready: "Aktivasyon hazır",
    activating: "Etkinleştiriliyor",
    active: "Başarılı",
    failed: "Başarısız",
    cancelled: "İptal edildi",
    superseded: "Yeni nesil geçti",
    reconciling: "Durum doğrulanıyor",
  };
  return labels[status];
}

function deploymentKindLabel(kind: DeploymentView["kind"]): string {
  if (kind === "rollback") return "Geri dönüş";
  if (kind === "preview") return "Önizleme";
  return "Dağıtım";
}

function previewStatusCode(status: PreviewView["status"]): StatusCode {
  if (status === "active") return "succeeded";
  if (status === "failed") return "failed";
  if (status === "removed") return "cancelled";
  if (status === "reconciling") return "reconciling";
  if (status === "requested") return "queued";
  return "running";
}

function previewStatusLabel(status: PreviewView["status"]): string {
  const labels: Readonly<Record<PreviewView["status"], string>> = {
    requested: "Kabul edildi",
    active: "Başarılı",
    closing: "Kapatılıyor",
    removed: "Kaldırıldı",
    failed: "Başarısız",
    reconciling: "Durum doğrulanıyor",
  };
  return labels[status];
}

function problemMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return `${error.problem.detail ?? error.problem.title} İstek kimliği: ${error.problem.requestId}`;
  }
  return error instanceof Error
    ? error.message
    : "İşlem tamamlanamadı. Bağlantınızı kontrol edip yeniden deneyin.";
}
